import React, { useState, useEffect } from 'react';
import styles from './widgetStyles.module.css';
import menuStyles from './selectionMenu.module.css';
import { getTopMusiciansFromUsername } from '../utils/api';
import { getSpotifyAuthUrl, getTopArtists } from '../utils/spotifyApi';


const SelectionMenu = ({ setInitialMusicians, setUsername, isVisible }) => {

    const [searchType, setSearchType] = useState('custom list');

    // Last.fm specific states
    const [lastfmUsername, setLastfmUsername] = useState('');
    const [timeperiod, setTimeperiod] = useState('overall');
    const [topCount, setTopCount] = useState('5');

    // Spotify specific states
    const [spotifyConnected, setSpotifyConnected] = useState(false);
    const [spotifyTimeRange, setSpotifyTimeRange] = useState('medium_term');
    const [spotifyLimit, setSpotifyLimit] = useState('10');


    // Custom list state
    const [customList, setCustomList] = useState('');

    // Loading state
    const [isLoading, setIsLoading] = useState(false);



    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            let musicians = [];

            switch (searchType) {
                case 'Last.fm':
                    if (!lastfmUsername) {
                        alert('Please enter a Last.fm username');
                        return;
                    }
                    const lastfmData = await getTopMusiciansFromUsername(lastfmUsername, topCount, timeperiod)

                    musicians = lastfmData;
                    break;

                case 'Spotify':
                    if (!spotifyConnected) {
                        alert('Please connect your Spotify account');
                        return;
                    }
                    // Fetch top artists from Spotify API
                    const spotifyData = await getTopArtists(spotifyTimeRange, spotifyLimit);
                    console.log(spotifyData);
                    musicians = spotifyData;

                    break;

                case 'custom list':
                    if (!customList.trim()) {
                        alert('Please enter a custom list');
                        return;
                    }

                    // Check for mock data commands
                    const mockMatch = customList.trim().match(/^mock\s*(\d+)$/i);
                    if (mockMatch) {
                        const nodeCount = parseInt(mockMatch[1]);
                        if (nodeCount > 30) {
                            alert('Too many mock nodes. Please use a maximum of 30.');
                            return;
                        }
                        if (nodeCount < 1) {
                            alert('Please enter a valid number greater than 0.');
                            return;
                        }
                        // Create mock data indicator that the grapher will recognize
                        musicians = [`__MOCK_DATA__${nodeCount}`];
                        console.log(`Creating mock data with ${nodeCount} nodes`);
                    } else {
                        // Process custom list (assuming semicolon-separated values)
                        musicians = customList.split(';').map(name => (name.trim()));
                        console.log(musicians);
                    }
                    break;

            }

            // Check if there are more than 30 musicians
            if (musicians.length > 30) {
                alert('Too many musicians selected. Please select a maximum of 30 musicians.');
                return;
            }

            // Update parent component with processed musicians
            setInitialMusicians(musicians);

        } catch (error) {
            console.error('Error processing selection:', error);
            alert('An error occurred while processing your selection');
        } finally {
            setIsLoading(false);
        }
    };


    // Input handlers
    const handleLastfmUsernameChange = (e) => {
        setUsername(e.target.value);
        setLastfmUsername(e.target.value);
    };

    const handleTimeperiodChange = (e) => {
        setTimeperiod(e.target.value);
    };

    const handleTopCountChange = (e) => {
        setTopCount(e.target.value);
    };

    const handleCustomListChange = (e) => {
        setCustomList(e.target.value);

    };

    const handleSpotifyConnect = () => {
        window.location.href = getSpotifyAuthUrl();
    };


    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const token = hash.substring(1).split("&").find(elem => elem.startsWith("access_token")).split("=")[1];
            window.location.hash = "";
            localStorage.setItem("spotify_token", token);
            setSpotifyConnected(true);
        }
    }, []);

    useEffect(() => {
        if (spotifyConnected) {
            setSearchType('Spotify');
        }
    }, [spotifyConnected]);

    const selectedColor = '#ffffff';
    const unselectedColor = '#888888';

    return (
        <div
            className={menuStyles.menuContainer}
            style={{
                opacity: isVisible ? 1 : 0,
                visibility: isVisible ? 'visible' : 'hidden',
            }}
        >
            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <h1 className={styles.musicianGrapherTitle}>Music Grapher</h1>
            </div>

            <div className={`${styles.menuTabs} ${menuStyles.tabBar}`}>
                <span
                    className={menuStyles.tab}
                    style={{
                        color: searchType === 'custom list' ? selectedColor : unselectedColor,
                        textDecoration: searchType === 'custom list' ? 'underline' : 'none',
                    }}
                    onClick={() => setSearchType('custom list')}
                >
                    Custom list
                </span>

                <span className={menuStyles.tabDivider}> | </span>
                <span
                    className={menuStyles.tab}
                    style={{
                        color: searchType === 'Last.fm' ? selectedColor : unselectedColor,
                        textDecoration: searchType === 'Last.fm' ? 'underline' : 'none',
                    }}
                    onClick={() => setSearchType('Last.fm')}
                >
                    Last.fm
                </span>

            </div>

            {/* if searchType is lastfm show username input section */}
            {searchType === 'Last.fm' && (
                <div className={menuStyles.formSection}>
                    <h2 className={menuStyles.sectionHeading}>
                        Enter your Last.fm username:
                    </h2>
                    <input
                        type="text"
                        className={styles.input}
                        value={lastfmUsername}
                        onChange={handleLastfmUsernameChange}
                        style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
                    />

                    <select
                        className={styles.input}
                        value={timeperiod}
                        onChange={handleTimeperiodChange}
                        style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
                    >
                        <option value="7day">7 days</option>
                        <option value="1month">1 month</option>
                        <option value="3month">3 months</option>
                        <option value="6month">6 months</option>
                        <option value="12month">12 months</option>
                        <option value="overall">overall</option>
                    </select>

                    <select
                        className={styles.input}
                        value={topCount}
                        onChange={handleTopCountChange}
                        style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
                    >
                        <option value="5">top 5 artists</option>
                        <option value="10">top 10 artists</option>
                        <option value="20">top 20 artists</option>
                    </select>
                </div>
            )}

            {/* if searchType is Spotify show Spotify login button */}
            {searchType === 'Spotify' && (
                <div className={menuStyles.formSection}>
                    {!spotifyConnected ? (
                        <>
                            <h2 className={menuStyles.sectionHeading}>
                                Connect your Spotify account:
                            </h2>
                            <button
                                className={styles.button}
                                onClick={handleSpotifyConnect}
                                style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
                            >
                                connect
                            </button>
                        </>
                    ) : (
                        <>
                            <h2 className={menuStyles.sectionHeading}>
                                Connected to Spotify
                            </h2>
                            <select
                                className={styles.input}
                                value={spotifyTimeRange}
                                onChange={(e) => setSpotifyTimeRange(e.target.value)}
                                style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
                            >
                                <option value="short_term">Last 4 weeks</option>
                                <option value="medium_term">Last 6 months</option>
                                <option value="long_term">All time</option>
                            </select>
                            <select
                                className={styles.input}
                                value={spotifyLimit}
                                onChange={(e) => setSpotifyLimit(e.target.value)}
                                style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
                            >
                                <option value="5">Top 5 artists</option>
                                <option value="10">Top 10 artists</option>
                                <option value="20">Top 20 artists</option>
                            </select>
                        </>
                    )}
                </div>
            )}

            {/* if searchType is custom list show custom list input */}
            {searchType === 'custom list' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h2 className={menuStyles.sectionHeading}>
                        Enter a custom list:
                    </h2>
                    <p style={{ margin: '0', fontSize: '14px', color: '#cccccc' }}>
                        Separate artists with a semicolon
                    </p>
                    <textarea
                        rows="4"
                        className={`${styles.input} ${menuStyles.textarea}`}
                        value={customList}
                        onChange={handleCustomListChange}
                        placeholder='e.g. Portishead; Björk; Tyler, The Creator; Slint'
                    />
                </div>
            )}

            <div style={{ marginTop: '20px' }}>
                <button
                    className={styles.button}
                    onClick={handleSubmit}
                    disabled={isLoading}
                    style={{
                        width: '100%',
                        maxWidth: 'none',
                        boxSizing: 'border-box',
                        opacity: isLoading ? 0.7 : 1,
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isLoading ? 'Creating...' : 'create network'}
                </button>
            </div>

        </div>
    );
};

export default SelectionMenu;
